import { z } from 'zod';
/**
 * Answers the internet check contradicts (task 72, decision 0048). When an outside-in reading gives a
 * control less credit than the business's own answer (it said "no remote desktop", port 3389 was seen open
 * on a date), the contradiction goes to review. The underwriter confirms it (the reading becomes the
 * answer, so the credit goes and a kill switch can apply) or dismisses it; the business can dispute it
 * first, with a reason and evidence, and the underwriter decides the dispute.
 */
export const CONFLICT_STATES = ['open', 'disputed', 'confirmed', 'dismissed', 'upheld', 'resolved'];
export const CONFLICT_STATE_LABEL = {
    open: 'To review',
    disputed: 'Disputed',
    confirmed: 'Confirmed',
    dismissed: 'Dismissed',
    upheld: 'Dispute upheld',
    resolved: 'Answer changed',
};
export const CONFLICT_STATE_TONE = {
    open: 'warn',
    disputed: 'warn',
    confirmed: 'bad',
    dismissed: 'neutral',
    upheld: 'ok',
    resolved: 'ok',
};
/** Still waiting for someone. */
export const CONFLICT_OPEN = ['open', 'disputed'];
export const DISPUTE_REASONS = ['not_ours', 'fixed', 'compensating_control'];
export const DISPUTE_REASON_LABEL = {
    not_ours: 'That isn’t ours (another company’s system, or not in scope)',
    fixed: 'It was fixed since the check',
    compensating_control: 'Something else covers it (a compensating control)',
};
export const Dispute = z.strictObject({
    reason: z.enum(DISPUTE_REASONS),
    note: z.string().trim().min(10, 'Say what happened, in a sentence or two').max(2000),
    documents: z.array(z.uuid()).max(10).default([]),
});
/** Confirm or dismiss a conflict; uphold or reject a dispute. */
export const DecideConflict = z.strictObject({
    outcome: z.enum(['confirm', 'dismiss', 'uphold', 'reject']),
    note: z.string().trim().max(2000).default(''),
});
/**
 * The credit an answer gives a control (1 in place, 0.5 partly, 0 not), read from the chosen option's
 * score effect; null when the option doesn't score that control.
 */
export function answeredCredit(q, value, control) {
    const o = q.options.find((x) => x.value === value);
    const e = o?.effects.find((x) => x.type === 'score_change' && x.params.control === control);
    const n = e ? Number(e.params.implementation) : NaN;
    return Number.isFinite(n) ? n : null;
}
/** A reading contradicts an answer when it gives the control less credit than the answer does. */
export const contradicts = (credit, reading) => credit !== null && reading < credit;
/** What a decision makes of a conflict. */
export function decided(state, outcome) {
    if (state === 'open' && outcome === 'confirm')
        return 'confirmed';
    if (state === 'open' && outcome === 'dismiss')
        return 'dismissed';
    if (state === 'disputed' && outcome === 'uphold')
        return 'upheld';
    if (state === 'disputed' && outcome === 'reject')
        return 'confirmed';
    return null;
}
//# sourceMappingURL=conflicts.js.map